import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon, LinkIcon, ExclamationTriangleIcon, CheckCircleIcon, UserIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { clientService } from '../services/clientService';
import { useAuth } from '../contexts/AuthContext';
import { useHeader } from '../contexts/HeaderContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { Pagination } from '../components/Pagination';
import ClientLookup from '../components/ClientLookup';
import { Client } from '../types';
import toast from 'react-hot-toast';

const INDUSTRIES = [
  "Aerospace, Defense & Government",
  "Associations & Organizations",
  "Automotive",
  "Banks",
  "Broadline Retail (New Name)",
  "CPG - Alcohol",
  "CPG - Food & Non-Alcoholic Beverages",
  "CPG - Household Items",
  "CPG - Personal Items (Luxury, Tobacco & Fashion)",
  "Commercial Services & Supplies",
  "Communications Equipment",
  "Construction",
  "Distributors",
  "Diversified REITs",
  "Diversified Telecommunication Services",
  "Electronic Equipment, Instruments & Components",
  "Energy, Gasoline & Oil",
  "Entertainment (Media, Sports, Theme Parks & Gambling)",
  "Financial Services & Banking",
  "Government",
  "Health Care REITs",
  "Healthcare Services",
  "Hotel & Resort REITs",
  "Household Appliances",
  "Household Durables",
  "IT Services",
  "Industrial REITs",
  "Insurance",
  "Leisure Products",
  "Marketing & Advertising",
  "Office REITs",
  "Pharmaceuticals",
  "Professional Services",
  "Real Estate",
  "Residential REITs",
  "Restaurants & QSR",
  "Retail",
  "Retail REITs",
  "Semiconductors & Semiconductor Equipment",
  "Shipping & Logistics",
  "Software",
  "Specialized REITs",
  "Specialty Retail",
  "Technology (Software, Hardware & Services)",
  "Technology Hardware, Storage & Peripherals",
  "Telecommunications",
  "Textiles, Apparel & Luxury Goods",
  "Travel, Leisure & Hospitality",
  "xFood Products",
  "xHousehold Products"
];

const Clients: React.FC = () => {
  const { user } = useAuth();
  const { setTitle } = useHeader();
  const navigate = useNavigate();

  useEffect(() => {
    setTitle('Client Management');
  }, [setTitle]);
  // Use custom claims permissions instead of role checks
  const hasClientsLookup = user?.permissions?.includes('clients:lookup') || user?.permissions?.includes('admin:full');
  const hasClientsAttachAdvisor = user?.permissions?.includes('clients:attach_advisor') || user?.permissions?.includes('admin:full');
  const hasClientsWrite = user?.permissions?.includes('clients:write') || user?.permissions?.includes('admin:full');
  
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const loadingToast = toast.loading('Importing clients...');
    try {
      const response = await clientService.importClients(file);
      const { summary } = response;
      toast.success(
        `Import complete! Created: ${summary.created}, Existing: ${summary.existing}, Skipped: ${summary.skipped}`,
        { id: loadingToast, duration: 5000 }
      );
      queryClient.invalidateQueries(['clients']);
    } catch (error: any) {
      toast.error(error.message || 'Failed to import clients', { id: loadingToast });
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showLookupModal, setShowLookupModal] = useState(false);
  const [showDuplicateCheck, setShowDuplicateCheck] = useState(false);
  const [potentialDuplicate, setPotentialDuplicate] = useState<Client | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  // For automatic duplicate detection in create form
  const [duplicateSearchQuery, setDuplicateSearchQuery] = useState('');
  const [debouncedDuplicateQuery, setDebouncedDuplicateQuery] = useState('');
  const [potentialMatches, setPotentialMatches] = useState<Client[]>([]);
  const [showBypassButton, setShowBypassButton] = useState(false);
  const [userAcknowledgedNewClient, setUserAcknowledgedNewClient] = useState(false);

  // Status Change Modal State
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [clientToUpdateStatus, setClientToUpdateStatus] = useState<Client | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    industry: '',
    companySize: '',
    status: 'active'
  });

  const industriesOptions = useMemo(() => {
    if (formData.industry && !INDUSTRIES.includes(formData.industry)) {
      return [formData.industry, ...INDUSTRIES];
    }
    return INDUSTRIES;
  }, [formData.industry]);

  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Fetch clients
  const { data: clientsData, isLoading } = useQuery({
    queryKey: ['clients', { search: searchTerm, page, limit }],
    queryFn: () => clientService.getClients({ search: searchTerm, page, limit }),
  });

  const clients = clientsData?.clients || [];
  const pagination = clientsData?.pagination;

  // Debounce search input for client search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1); // Reset page to 1 on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Call-ahead prefetching in background
  useEffect(() => {
    if (pagination && page < pagination.pages) {
      const nextPage = page + 1;
      queryClient.prefetchQuery({
        queryKey: ['clients', { search: searchTerm, page: nextPage, limit }],
        queryFn: () => clientService.getClients({ search: searchTerm, page: nextPage, limit }),
      });
    }
  }, [page, limit, searchTerm, pagination, queryClient]);

  // Debounce duplicate search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDuplicateQuery(duplicateSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [duplicateSearchQuery]);

  // Auto-search for duplicates when advisor types in name field
  const { data: duplicateSearchResults, isLoading: isSearchingDuplicates } = useQuery({
    queryKey: ['clientDuplicateSearch', debouncedDuplicateQuery],
    queryFn: () => clientService.lookupClients(debouncedDuplicateQuery),
    enabled: hasClientsLookup && !editingClient && showCreateForm && debouncedDuplicateQuery.length >= 3,
    staleTime: 0,
  });

  // Update potential matches when search results change
  useEffect(() => {
    if (duplicateSearchResults && duplicateSearchResults.length > 0) {
      setPotentialMatches(duplicateSearchResults);
      setShowBypassButton(true);
      setUserAcknowledgedNewClient(false); // Reset acknowledgment when new matches are found
    } else if (debouncedDuplicateQuery.length >= 3 && !isSearchingDuplicates) {
      setPotentialMatches([]);
      setShowBypassButton(false);
      setUserAcknowledgedNewClient(true); // No matches means it's safe to create
    }
  }, [duplicateSearchResults, debouncedDuplicateQuery, isSearchingDuplicates]);

  // Create client mutation
  const createClientMutation = useMutation({
    mutationFn: clientService.createClient,
    onSuccess: () => {
      queryClient.invalidateQueries(['clients'], { refetchActive: true, refetchInactive: true });
      setShowCreateForm(false);
      resetForm();
      toast.success('Client created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create client');
    }
  });

  // Update client mutation
  const updateClientMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Client> }) =>
      clientService.updateClient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['clients'], { refetchActive: true, refetchInactive: true });
      setShowCreateForm(false);
      setEditingClient(null);
      resetForm();
      toast.success('Client updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update client');
    }
  });

  // Delete client mutation
  const deleteClientMutation = useMutation({
    mutationFn: clientService.deleteClient,
    onSuccess: () => {
      queryClient.invalidateQueries(['clients'], { refetchActive: true, refetchInactive: true });
      toast.success('Client deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete client');
    }
  });

  // Attach advisor to client mutation
  const attachAdvisorMutation = useMutation({
    mutationFn: clientService.attachAdvisor,
    onSuccess: () => {
      queryClient.invalidateQueries(['clients'], { refetchActive: true, refetchInactive: true });
      setShowLookupModal(false);
      toast.success('Successfully attached to client');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to attach to client');
    },
  });


  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      website: '',
      industry: '',
      companySize: '',
      status: 'active'
    });
    setPotentialMatches([]);
    setShowBypassButton(false);
    setDuplicateSearchQuery('');
    setDebouncedDuplicateQuery('');
    setUserAcknowledgedNewClient(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingClient) {
      updateClientMutation.mutate({ id: editingClient.id, data: formData });
    } else {
      createClientMutation.mutate(formData);
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      website: client.website || '',
      industry: client.industry || '',
      companySize: client.companySize || '',
      status: client.status
    });
    setShowCreateForm(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      deleteClientMutation.mutate(id);
    }
  };

  // Removed early page-level isLoading spinner return to prevent unmounting search inputs.
  // Loading state is now handled downstream inside the clients list container.

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your marketing clients
          </p>
        </div>
        <div className="flex space-x-2">
          {hasClientsLookup && (
            <button
              onClick={() => setShowLookupModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Lookup
            </button>
          )}
          {hasClientsWrite && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".csv"
                onChange={handleFileChange}
              />
              <button
                onClick={handleImportClick}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
                Import CSV
              </button>
            </>
          )}
          <button
            onClick={() => {
              setEditingClient(null);
              resetForm();
              setShowCreateForm(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            New Client
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search clients..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Clients List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Clients</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : clients.length > 0 ? (
            clients.map((client: Client) => (
              <div key={client.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{client.name}</h4>
                    <p className="text-sm text-gray-500">{client.email}</p>
                    {client.industry && (
                      <p className="text-xs text-gray-400 mt-1">Industry: {client.industry}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        // Check if user is admin or super_admin
                        const canManageStatus = user?.role === 'admin' || user?.role === 'super_admin';
                        if (canManageStatus) {
                          setClientToUpdateStatus(client);
                          setShowStatusModal(true);
                        }
                      }}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${client.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                        } ${(user?.role === 'admin' || user?.role === 'super_admin') ? 'cursor-pointer hover:opacity-80' : ''}`}
                      title={(user?.role === 'admin' || user?.role === 'super_admin') ? 'Click to change status' : ''}
                    >
                      {client.status}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/users?client_id=${client.id}`);
                      }}
                      className="text-gray-600 hover:text-blue-900 p-1"
                      title="View Users"
                    >
                      <UserIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(client)}
                      className="text-blue-600 hover:text-blue-900 p-1"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    {(!client.assessmentCount || client.assessmentCount === 0) && (
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="text-red-600 hover:text-red-900 p-1"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center">
              <h3 className="text-sm font-medium text-gray-900">No clients found</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new client.</p>
            </div>
          )}
        </div>
        {pagination && (
          <Pagination
            page={page}
            limit={limit}
            total={pagination.total}
            totalPages={pagination.pages}
            onPageChange={setPage}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
          />
        )}
      </div>

      {/* Create/Edit Client Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingClient ? 'Edit Client' : 'Create New Client'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({ ...formData, name: value });
                        // Trigger duplicate search for advisors
                        if (hasClientsLookup && !editingClient) {
                          setDuplicateSearchQuery(value);
                        }
                      }}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${hasClientsLookup && !editingClient && potentialMatches.length > 0
                        ? 'border-yellow-300 bg-yellow-50'
                        : 'border-gray-300'
                        }`}
                      required
                    />
                    {hasClientsLookup && !editingClient && potentialMatches.length > 0 && (
                      <ExclamationTriangleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-yellow-500" />
                    )}
                  </div>

                  {/* Automatic duplicate warning and matches */}
                  {hasClientsLookup && !editingClient && (
                    <>
                      {isSearchingDuplicates && debouncedDuplicateQuery.length >= 3 && (
                        <div className="mt-2 flex items-center text-sm text-gray-600">
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">Checking for existing clients...</span>
                        </div>
                      )}

                      {potentialMatches.length > 0 && !isSearchingDuplicates && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                          <div className="flex items-start">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-yellow-800 mb-2">
                                Similar clients found. Please review:
                              </p>
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                {potentialMatches.map((client) => (
                                  <div
                                    key={client.id}
                                    onClick={async () => {
                                      // Show attach modal and close create form
                                      setPotentialDuplicate(client);
                                      setShowDuplicateCheck(true);
                                      setShowCreateForm(false);
                                    }}
                                    className="p-2 bg-white border border-yellow-300 rounded cursor-pointer hover:bg-yellow-100 transition-colors"
                                  >
                                    <div className="font-medium text-gray-900">{client.name}</div>
                                    {client.email && (
                                      <div className="text-sm text-gray-600">{client.email}</div>
                                    )}
                                    {client.industry && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        Industry: {client.industry}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                              {showBypassButton && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setUserAcknowledgedNewClient(true);
                                    setShowBypassButton(false);
                                  }}
                                  className="mt-3 w-full text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                                >
                                  This is a new client - continue creating
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Industry</label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select industry</option>
                    {industriesOptions.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingClient(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    Cancel
                  </button>
                  {(() => {
                    const isCreateBlockedByDuplicates = hasClientsLookup && !editingClient && potentialMatches.length > 0 && !userAcknowledgedNewClient;
                    return (
                      <button
                        type="submit"
                        disabled={
                          createClientMutation.isLoading ||
                          updateClientMutation.isLoading ||
                          isCreateBlockedByDuplicates
                        }
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {createClientMutation.isLoading || updateClientMutation.isLoading
                          ? 'Saving...'
                          : editingClient ? 'Update Client' : 'Create Client'
                        }
                      </button>
                    );
                  })()}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Client Lookup Modal (for users with clients:lookup permission) */}
      {showLookupModal && hasClientsLookup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Lookup and Attach to Client
              </h3>
              <ClientLookup
                onSelect={async (client) => {
                  if (window.confirm(`Attach yourself to ${client.name}?`)) {
                    attachAdvisorMutation.mutate(client.id);
                  }
                }}
                onCancel={() => setShowLookupModal(false)}
                placeholder="Search for a client (minimum 3 characters)..."
              />
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowLookupModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attach to Existing Client Modal */}
      {showDuplicateCheck && potentialDuplicate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Attach to Existing Client</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-2">
                You selected an existing client:
              </p>
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="font-medium text-gray-900">{potentialDuplicate.name}</p>
                {potentialDuplicate.email && (
                  <p className="text-sm text-gray-600 mt-1">{potentialDuplicate.email}</p>
                )}
                {potentialDuplicate.industry && (
                  <p className="text-xs text-gray-500 mt-1">Industry: {potentialDuplicate.industry}</p>
                )}
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-700">
                Would you like to attach yourself to this client? This will give you access to manage this client.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDuplicateCheck(false);
                  setPotentialDuplicate(null);
                  // Reopen create form if it was closed
                  if (!showCreateForm) {
                    setShowCreateForm(true);
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              {hasClientsAttachAdvisor && (
                <button
                  onClick={async () => {
                    try {
                      await attachAdvisorMutation.mutateAsync(potentialDuplicate.id);
                      setShowDuplicateCheck(false);
                      setPotentialDuplicate(null);
                      setShowCreateForm(false);
                      resetForm();
                    } catch (error) {
                      // Error already handled by mutation
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                  disabled={attachAdvisorMutation.isLoading}
                >
                  {attachAdvisorMutation.isLoading ? 'Attaching...' : 'Attach to Client'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && clientToUpdateStatus && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Update Client Status</h3>
            <p className="text-sm text-gray-500 mb-4">
              Select the new status for <strong>{clientToUpdateStatus.name}</strong>.
            </p>
            <div className="space-y-2">
              {[
                { value: 'active', label: 'Active', description: 'Client can access system and create evaluations' },
                { value: 'inactive', label: 'Inactive', description: 'Client access restricted' }
              ].map((statusOption) => (
                <button
                  key={statusOption.value}
                  onClick={() => {
                    if (clientToUpdateStatus.status !== statusOption.value) {
                      updateClientMutation.mutate({
                        id: clientToUpdateStatus.id,
                        data: { status: statusOption.value }
                      });
                    }
                    setShowStatusModal(false);
                    setClientToUpdateStatus(null);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-md border ${clientToUpdateStatus.status === statusOption.value
                    ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                    : 'border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className={`text-sm font-medium ${clientToUpdateStatus.status === statusOption.value ? 'text-blue-900' : 'text-gray-900'
                        }`}>
                        {statusOption.label}
                      </p>
                      <p className={`text-xs ${clientToUpdateStatus.status === statusOption.value ? 'text-blue-700' : 'text-gray-500'
                        }`}>
                        {statusOption.description}
                      </p>
                    </div>
                    {clientToUpdateStatus.status === statusOption.value && (
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setClientToUpdateStatus(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
